import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Alert,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Link,
  LinearProgress,
  Skeleton
} from '@mui/material'
import PeopleIcon from '@mui/icons-material/People'
import VerifiedIcon from '@mui/icons-material/Verified'
import SubscriptionsIcon from '@mui/icons-material/Subscriptions'
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import MusicNoteIcon from '@mui/icons-material/MusicNote'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import PaidIcon from '@mui/icons-material/Paid'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import PendingIcon from '@mui/icons-material/Pending'
import DoDisturbIcon from '@mui/icons-material/DoDisturb'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import RefreshIcon from '@mui/icons-material/Refresh'
import TimerIcon from '@mui/icons-material/Timer'
import TodayIcon from '@mui/icons-material/Today'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts'
import urls from '../constants/apiurl'
import ApiCaller from '../constants/ApiCaller'
import { showDangerToast } from '../constants/toaster'
import { formatIndianNumber } from '../lib/formatIndianNumber'
import {
  updateDashboardData,
  updatePaymentsData,
  updateDauData,
  updatePeakData
} from '../../redux/features/common/commonSlice'
import {
  selectDashboardData,
  selectPaymentsData,
  selectDauData,
  selectPeakData
} from '../../redux/features/common/selectors'
import InsufficientPermission from '../custom/InSufficientPermission'
import { fetchPaymentsSummary } from '../services/dashboardPaymentsService'
import './DashboardOverview.css'


const CHART_COLORS = ['#5e72e4', '#11cdef', '#2dce89', '#fb6340', '#f5365c', '#8965e0']

const parseMetricValue = value => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value
  if (value == null || value === '') return 0
  const raw = String(value).replace(/,/g, '').trim()
  const match = raw.match(/^([\d.]+)\s*([KMB])?/i)
  if (!match) {
    const n = parseFloat(raw)
    return Number.isNaN(n) ? 0 : n
  }
  let num = parseFloat(match[1])
  const suffix = (match[2] || '').toUpperCase()
  if (suffix === 'K') num *= 1000
  if (suffix === 'M') num *= 1000000
  if (suffix === 'B') num *= 1000000000
  return num
}

const formatCompact = value => {
  const n = parseMetricValue(value)
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
  return formatIndianNumber(n, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

const MetricCard = ({
  label,
  value,
  sub,
  icon,
  accent = '#5e72e4',
  variant = 'default',
  footerLink,
  footerHref,
  className = '',
  compact = false
}) => {
  const isTrending = variant === 'trending'
  const hasSub = Boolean(sub && String(sub).trim())
  const hasFooter = Boolean(footerLink && footerHref)

  return (
    <Card
      className={`dashboard-metric-card${compact ? ' dashboard-metric-card--compact' : ''} ${isTrending ? `dashboard-metric-card--trending ${className}` : className}`}
      sx={
        isTrending
          ? { borderTop: 'none' }
          : { borderTop: `3px solid ${accent}`, bgcolor: '#fff' }
      }
    >
      <CardContent>
        <div className="dashboard-metric-card__row">
          <div className="dashboard-metric-card__main">
            <Typography
              component="p"
              variant="caption"
              color={isTrending ? 'inherit' : 'text.secondary'}
              className="dashboard-metric-card__label"
            >
              {label}
            </Typography>
            <Typography
              component="p"
              variant="h4"
              color={isTrending ? 'inherit' : 'text.primary'}
              className="dashboard-metric-card__value"
            >
              {value}
            </Typography>
            <Typography
              component="p"
              variant="body2"
              color={isTrending ? 'inherit' : 'text.secondary'}
              className={`dashboard-metric-card__sub${hasSub ? '' : ' dashboard-metric-card__sub--empty'}`}
              noWrap={isTrending}
              title={hasSub ? sub : undefined}
            >
              {hasSub ? sub : '\u00A0'}
            </Typography>
          </div>
          {icon && (
            <div
              className="dashboard-metric-card__icon"
              style={
                isTrending
                  ? undefined
                  : { backgroundColor: `${accent}18`, color: accent }
              }
            >
              {icon}
            </div>
          )}
        </div>
        <div
          className={`dashboard-metric-card__footer${hasFooter ? '' : ' dashboard-metric-card__footer--empty'}`}
        >
          {hasFooter && (
            <Link href={footerHref} target="_blank" rel="noreferrer">
              {footerLink}
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

const StatCard = props => <MetricCard {...props} />

const ChartTooltipStyle = {
  borderRadius: 8,
  border: '1px solid #e9ecef',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
}

const RevenueComparePanel = ({ items }) => {
  const maxAmount = Math.max(...items.map(item => item.amount), 1)

  return (
    <Box sx={{ px: 1, py: 0.5 }}>
      {items.map((item, index) => {
        const barPercent = Math.max((item.amount / maxAmount) * 100, item.amount > 0 ? 4 : 0)
        return (
          <Box key={item.name} sx={{ mb: index < items.length - 1 ? 2.5 : 0 }}>
            <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={1} mb={0.75}>
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                {item.name}
              </Typography>
              <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ whiteSpace: 'nowrap' }}>
                {item.display}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={barPercent}
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: '#e9ecef',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  bgcolor: item.color
                }
              }}
            />
          </Box>
        )
      })}
    </Box>
  )
}

const insightGridProps = { xs: 6, sm: 4, md: 4, lg: 2 }

const paymentsGridProps = { xs: 12, sm: 6, md: 3, lg: 3 }

const formatPaymentMetric = value =>
  value == null
    ? '--'
    : formatIndianNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 })

const getPaymentSubLabel = value => (value == null ? 'No data' : 'Total')

const PaymentsSummaryLoading = () => (
  <Grid container spacing={1.5} className="dashboard-grid" sx={{ mb: 1.5 }}>
    {Array.from({ length: 8 }).map((_, index) => (
      <Grid item {...paymentsGridProps} key={`payments-loading-${index}`}>
        <Card className="dashboard-metric-card" sx={{ borderTop: '3px solid #e9ecef', bgcolor: '#fff' }}>
          <CardContent>
            <Skeleton variant="text" width="60%" height={18} />
            <Skeleton variant="text" width="45%" height={36} sx={{ mt: 1 }} />
            <Skeleton variant="text" width="50%" height={16} sx={{ mt: 0.5 }} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
)

const PaymentsSummaryError = ({ onRetry }) => (
  <Card className="dashboard-chart-card dashboard-payments-error-card">
    <CardContent>
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={onRetry}>
            Retry
          </Button>
        }
      >
        Unable to load payments summary right now.
      </Alert>
    </CardContent>
  </Card>
)

const MoreInsightsSection = ({ dashboardData }) => (
  <>
    <p className="dashboard-section-title">More insights</p>
    <Grid container spacing={1.25} className="dashboard-grid dashboard-insights-grid" sx={{ mb: 1.5 }}>
      <Grid item {...insightGridProps}>
        <StatCard
          compact
          accent="#8965e0"
          icon={<PendingActionsIcon sx={{ fontSize: 18 }} />}
          label="Badge requests"
          value={formatIndianNumber(
            parseMetricValue(dashboardData?.totalNumberOfBadgeVerificationRequest),
            { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          )}
          sub="Pending"
        />
      </Grid>
      <Grid item {...insightGridProps}>
        <StatCard
          compact
          accent="#172b4d"
          icon={<VideoLibraryIcon sx={{ fontSize: 18 }} />}
          label="Uploaded videos"
          value={dashboardData?.totalNumberOfUploadedVideos || '0'}
          sub="Total"
        />
      </Grid>
      <Grid item {...insightGridProps}>
        <StatCard
          compact
          accent="#5e72e4"
          icon={<VideoLibraryIcon sx={{ fontSize: 18 }} />}
          label="Videos uploaded today"
          value={formatIndianNumber(
            parseMetricValue(dashboardData?.totalNumberOfUploadedVideosToday),
            { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          )}
          sub="Today"
        />
      </Grid>
      <Grid item {...insightGridProps}>
        <StatCard
          compact
          accent="#2dce89"
          icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
          label="Videos uploaded this week"
          value={formatIndianNumber(
            parseMetricValue(dashboardData?.totalNumberOfUploadedVideosWeek),
            { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          )}
          sub="Last 7 days"
        />
      </Grid>
      <Grid item {...insightGridProps}>
        <StatCard
          compact
          accent="#11cdef"
          icon={<PeopleIcon sx={{ fontSize: 18 }} />}
          label="Verified users"
          value={dashboardData?.totalNumberOfVerifiedUsers || '0'}
          sub="Accounts"
        />
      </Grid>

      <Grid item {...insightGridProps}>
        <MetricCard
          compact
          variant="trending"
          className="dashboard-metric-card--purple"
          icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
          label="Trending video"
          value={formatIndianNumber(dashboardData?.trendingVideoTotalViews ?? 0, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          })}
          sub="24h views"
          footerLink={dashboardData?.trendingVideoLink ? 'View' : undefined}
          footerHref={dashboardData?.trendingVideoLink}
        />
      </Grid>
      <Grid item {...insightGridProps}>
        <MetricCard
          compact
          variant="trending"
          className="dashboard-metric-card--teal"
          icon={<MusicNoteIcon sx={{ fontSize: 18 }} />}
          label="Trending sound"
          value={formatIndianNumber(dashboardData?.trendingSoundTotalUsed ?? 0, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          })}
          sub={dashboardData?.trendingSoundName || '—'}
          footerLink={dashboardData?.trendingSoundLink ? 'View' : undefined}
          footerHref={dashboardData?.trendingSoundLink}
        />
      </Grid>
    </Grid>
  </>
)

const formatHoursAndMinutes = (value) => {
  if (value == null || value === '') return '—'
  const numericVal = parseFloat(value)
  if (Number.isNaN(numericVal)) return value

  const totalMinutes = Math.round(numericVal * 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  if (hours === 0) {
    return `${minutes}m`
  }
  return `${hours}h ${minutes}m`
}

const ActiveTimeSection = ({ dashboardData }) => {
  const cards = [
    {
      key: 'totalTimeSpent',
      label: 'Total time spent',
      accent: '#8965e0',
      icon: <AccessTimeIcon sx={{ fontSize: 18 }} />,
      sub: 'Cumulative time'
    },
    {
      key: 'averageTimeSpent',
      label: 'Average time spent',
      accent: '#11cdef',
      icon: <TimerIcon sx={{ fontSize: 18 }} />,
      sub: 'Per session'
    },
    {
      key: 'averageActiveTimePerDay',
      label: 'Average active time per day',
      accent: '#2dce89',
      icon: <TodayIcon sx={{ fontSize: 18 }} />,
      sub: 'Daily average'
    },
    {
      key: 'averageActiveTimePerWeek',
      label: 'Average active time per week',
      accent: '#fb6340',
      icon: <CalendarMonthIcon sx={{ fontSize: 18 }} />,
      sub: 'Weekly average'
    }
  ]

  return (
    <Grid container spacing={1.5} className="dashboard-grid" sx={{ mb: 1.5 }}>
      {cards.map(card => {
        const metricValue = dashboardData?.[card.key]
        return (
          <Grid item xs={12} sm={6} md={3} lg={3} key={card.key}>
            <StatCard
              accent={card.accent}
              icon={card.icon}
              label={card.label}
              value={formatHoursAndMinutes(metricValue)}
              sub={card.sub}
            />
          </Grid>
        )
      })}
    </Grid>
  )
}


const PaymentsSummarySection = ({ isLoading, error, paymentsData, onRetry }) => {
  const cards = [
    {
      key: 'totalUsersCashConvertableChips',
      label: 'Users cash-convertable chips',
      accent: '#5e72e4',
      icon: <AccountBalanceWalletIcon fontSize="small" />
    },
    {
      key: 'totalPayableAmountToUsers',
      label: 'Payable amount to users',
      accent: '#2dce89',
      icon: <CurrencyRupeeIcon fontSize="small" />
    },
    {
      key: 'totalPaidChipsToUsers',
      label: 'Paid chips to users',
      accent: '#11cdef',
      icon: <PaidIcon fontSize="small" />
    },
    {
      key: 'totalAmountPaidToUsers',
      label: 'Amount paid to users',
      accent: '#fb6340',
      icon: <CurrencyRupeeIcon fontSize="small" />
    },
    {
      key: 'bankPaidTotal',
      label: 'Bank paid total',
      accent: '#2dce89',
      icon: <AccountBalanceIcon fontSize="small" />
    },
    {
      key: 'bankRejectedTotal',
      label: 'Bank rejected total',
      accent: '#f5365c',
      icon: <DoDisturbIcon fontSize="small" />
    },
    {
      key: 'bankProcessingTotal',
      label: 'Bank processing total',
      accent: '#8965e0',
      icon: <AutorenewIcon fontSize="small" />
    },
    {
      key: 'bankPendingTotal',
      label: 'Bank pending total',
      accent: '#ffd600',
      icon: <PendingIcon fontSize="small" />
    }
  ]

  if (isLoading) return <PaymentsSummaryLoading />
  if (error) return <PaymentsSummaryError onRetry={onRetry} />

  return (
    <Grid container spacing={1.5} className="dashboard-grid" sx={{ mb: 1.5 }}>
      {cards.map(card => {
        const metricValue = paymentsData?.[card.key]
        return (
          <Grid item {...paymentsGridProps} key={card.key}>
            <StatCard
              accent={card.accent}
              icon={card.icon}
              label={card.label}
              value={formatPaymentMetric(metricValue)}
              sub={getPaymentSubLabel(metricValue)}
            />
          </Grid>
        )
      })}
    </Grid>
  )
}

const DashboardOverview = () => {
  const dispatch = useDispatch()
  const userData = useSelector(state => state?.userData)
  const userPermisiions = useSelector(state => state?.permission)

  const dashboardData = useSelector(selectDashboardData)
  const paymentsData = useSelector(selectPaymentsData)
  const dauData = useSelector(selectDauData)
  const peakData = useSelector(selectPeakData)

  const [pageLoading, setPageLoading] = useState(false)
  const [paymentsLoading, setPaymentsLoading] = useState(false)
  const [paymentsError, setPaymentsError] = useState('')
  const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day'))
  const [endDate, setEndDate] = useState(dayjs())

  const [pendingUpdates, setPendingUpdates] = useState(null)

  const dashboardDataRef = useRef(dashboardData)
  const paymentsDataRef = useRef(paymentsData)
  const dauDataRef = useRef(dauData)
  const peakDataRef = useRef(peakData)

  useEffect(() => { dashboardDataRef.current = dashboardData }, [dashboardData])
  useEffect(() => { paymentsDataRef.current = paymentsData }, [paymentsData])
  useEffect(() => { dauDataRef.current = dauData }, [dauData])
  useEffect(() => { peakDataRef.current = peakData }, [peakData])

  let viewPermission = true
  if (userPermisiions?.modules?.length > 0) {
    const currentModulePermission = userPermisiions.modules.find(
      element => element.moduleId === '1'
    )?.permissions
    viewPermission =
      currentModulePermission?.find(element => element.name === 'VIEW')?.isEnable ?? true
  }

  const fetchOverview = useCallback(async () => {
    setPageLoading(true)
    try {
      const [dashRes, analyticsRes] = await Promise.all([
        ApiCaller.get({}, urls.dashboard, userData?.access_token),
        ApiCaller.get({}, urls.analyticsToday, userData?.access_token)
      ])
      const dashJson = await dashRes.response.json()
      const analyticsJson = await analyticsRes.response.json()

      let dauCount = 0
      let totalLocationActive = 0
      if (analyticsJson.data || analyticsRes.response.status === 200) {
        dauCount = analyticsJson.data?.dauCount ?? 0
        totalLocationActive = analyticsJson.data?.totalLocationActive ?? 0
      }

      if (dashJson.status == 200 || dashRes.response.status === 200) {
        dispatch(updateDashboardData({
          ...dashJson.data,
          dauCount,
          totalLocationActive
        }))
      } else {
        showDangerToast(dashJson.message)
      }
    } catch (error) {
      console.error('Error fetching dashboard overview:', error)
      showDangerToast(error.message)
    } finally {
      setPageLoading(false)
    }
  }, [dispatch, userData?.access_token])

  const fetchPayments = useCallback(async () => {
    setPaymentsLoading(true)
    setPaymentsError('')
    try {
      const paymentsSummary = await fetchPaymentsSummary(userData?.access_token)
      dispatch(updatePaymentsData(paymentsSummary))
    } catch (error) {
      console.error('Error fetching payments summary:', error)
      setPaymentsError(error?.message || 'Unable to load payments summary')
    } finally {
      setPaymentsLoading(false)
    }
  }, [dispatch, userData?.access_token])

  const fetchCharts = useCallback(async (start = startDate, end = endDate) => {
    try {
      const from = start.format('YYYY-MM-DD')
      const to = end.format('YYYY-MM-DD')
      const date = (end || dayjs()).format('YYYY-MM-DD')
      const [dauRes, peakRes] = await Promise.all([
        ApiCaller.get({}, `${urls.analyticsDau}?from=${from}&to=${to}`, userData?.access_token),
        ApiCaller.get({}, `${urls.analyticsPeak}?date=${date}`, userData?.access_token)
      ])
      const dauJson = await dauRes.response.json()
      const peakJson = await peakRes.response.json()

      if (dauJson.data || dauRes.response.status === 200) {
        dispatch(updateDauData(dauJson.data?.data || []))
      }
      if (peakJson.data || peakRes.response.status === 200) {
        dispatch(updatePeakData(peakJson.data?.hourly || []))
      }
    } catch (error) {
      console.error('Error fetching dashboard charts:', error)
    }
  }, [dispatch, userData?.access_token, startDate, endDate])

  const fetchAllData = useCallback(async (start = startDate, end = endDate) => {
    setPendingUpdates(null)
    await Promise.all([
      fetchOverview(),
      fetchPayments(),
      fetchCharts(start, end)
    ])
  }, [fetchOverview, fetchPayments, fetchCharts, startDate, endDate])

  const fetchDashboardAndAnalyticsSilently = useCallback(async () => {
    const from = startDate.format('YYYY-MM-DD')
    const to = endDate.format('YYYY-MM-DD')
    const date = (endDate || dayjs()).format('YYYY-MM-DD')

    let liveDauCount = dashboardDataRef.current?.dauCount ?? 0
    let liveLocationActive = dashboardDataRef.current?.totalLocationActive ?? 0
    let newDashboardData = null
    let paymentsSummary = null

    const overviewPromise = (async () => {
      try {
        const [dashRes, analyticsRes] = await Promise.all([
          ApiCaller.get({}, urls.dashboard, userData?.access_token),
          ApiCaller.get({}, urls.analyticsToday, userData?.access_token)
        ])
        const dashJson = await dashRes.response.json()
        const analyticsJson = await analyticsRes.response.json()

        if (analyticsJson.data || analyticsRes.response.status === 200) {
          liveDauCount = analyticsJson.data?.dauCount ?? 0
          liveLocationActive = analyticsJson.data?.totalLocationActive ?? 0
        }

        const currentDashboard = { ...dashboardDataRef.current }
        dispatch(updateDashboardData({
          ...currentDashboard,
          dauCount: liveDauCount,
          totalLocationActive: liveLocationActive
        }))

        if (dashJson.status == 200 || dashRes.response.status === 200) {
          newDashboardData = {
            ...dashJson.data,
            dauCount: liveDauCount,
            totalLocationActive: liveLocationActive
          }
        }
      } catch (error) {
        console.error('Error silently fetching overview:', error)
      }
    })()

    const chartsPromise = (async () => {
      try {
        const [dauRes, peakRes] = await Promise.all([
          ApiCaller.get({}, `${urls.analyticsDau}?from=${from}&to=${to}`, userData?.access_token),
          ApiCaller.get({}, `${urls.analyticsPeak}?date=${date}`, userData?.access_token)
        ])
        const dauJson = await dauRes.response.json()
        const peakJson = await peakRes.response.json()

        if (dauJson.data || dauRes.response.status === 200) {
          dispatch(updateDauData(dauJson.data?.data || []))
        }
        if (peakJson.data || peakRes.response.status === 200) {
          dispatch(updatePeakData(peakJson.data?.hourly || []))
        }
      } catch (error) {
        console.error('Error silently fetching charts:', error)
      }
    })()

    const paymentsPromise = (async () => {
      // Mount path calls fetchPayments() when cache is empty — avoid duplicate request
      if (paymentsDataRef.current === null) return
      try {
        paymentsSummary = await fetchPaymentsSummary(userData?.access_token)
      } catch (error) {
        console.error('Error silently fetching payments:', error)
      }
    })()

    await Promise.all([overviewPromise, chartsPromise, paymentsPromise])

    const currentDashboard = { ...dashboardDataRef.current }
    const newCompareDash = { ...(newDashboardData || {}) }
    delete newCompareDash.dauCount
    delete newCompareDash.totalLocationActive
    const currentCompareDash = { ...currentDashboard }
    delete currentCompareDash.dauCount
    delete currentCompareDash.totalLocationActive

    const dashboardChanged =
      newDashboardData != null &&
      JSON.stringify(newCompareDash) !== JSON.stringify(currentCompareDash)
    const paymentsChanged =
      paymentsSummary != null &&
      JSON.stringify(paymentsSummary) !== JSON.stringify(paymentsDataRef.current)

    if (dashboardChanged || paymentsChanged) {
      setPendingUpdates({
        dashboardData: newDashboardData || currentDashboard,
        paymentsData: paymentsSummary != null ? paymentsSummary : paymentsDataRef.current
      })
    } else {
      setPendingUpdates(null)
    }
  }, [userData?.access_token, startDate, endDate, dispatch])

  const didMountRef = useRef(false)

  useEffect(() => {
    if (viewPermission && userData?.access_token && !didMountRef.current) {
      didMountRef.current = true
      // Partial cache: overview alone is enough to skip blocking reload
      const hasDashCache = dashboardData && Object.keys(dashboardData).length > 0

      if (hasDashCache) {
        fetchDashboardAndAnalyticsSilently()
        if (paymentsData === null) {
          fetchPayments()
        }
      } else {
        fetchAllData()
      }
    }
  }, [viewPermission, userData?.access_token])



  const platformMetrics = useMemo(
    () =>
      [
        {
          name: 'Verification badges',
          value: parseMetricValue(dashboardData?.totalNumberOfVerificationBadge),
          fill: CHART_COLORS[0]
        },
        {
          name: 'Subscribed channels',
          value: parseMetricValue(dashboardData?.totalNumberOfSubscribedChannel),
          fill: CHART_COLORS[1]
        },
        {
          name: 'Verified users',
          value: parseMetricValue(dashboardData?.totalNumberOfVerifiedUsers),
          fill: CHART_COLORS[2]
        },
        {
          name: 'Uploaded videos',
          value: parseMetricValue(dashboardData?.totalNumberOfUploadedVideos),
          fill: CHART_COLORS[3]
        },
        {
          name: 'Badge requests',
          value: parseMetricValue(dashboardData?.totalNumberOfBadgeVerificationRequest),
          fill: CHART_COLORS[4]
        }
      ].filter(item => item.value > 0 || item.name),
    [dashboardData]
  )

  const revenueMetrics = useMemo(
    () => [
      {
        name: 'Tips received',
        amount: parseMetricValue(dashboardData?.totalNumberOfTipReceived),
        display: `${dashboardData?.totalNumberOfTipReceived || '0'} Beads`,
        color: '#fb6340'
      },
      {
        name: 'Admin earnings',
        amount: parseMetricValue(dashboardData?.totalEarningOfAdminViaBeads),
        display: formatIndianNumber(dashboardData?.totalEarningOfAdminViaBeads ?? 0),
        color: '#5e72e4'
      }
    ],
    [dashboardData]
  )

  const handleFilter = () => {
    fetchAllData()
  }

  const handleReset = () => {
    const start = dayjs().subtract(7, 'day')
    const end = dayjs()
    setStartDate(start)
    setEndDate(end)
    fetchAllData(start, end)
  }

  if (!viewPermission) {
    return (
      <div className="container-fluid" style={{ marginTop: '2rem' }}>
        <InsufficientPermission />
      </div>
    )
  }

  return (
    <div className="dashboard-overview">
      {pageLoading && (
        <div className="dashboard-content-cover" aria-busy="true" aria-live="polite">
          <div className="cover-content">
            <div style={{ color: '#785ebb' }} className="la-square-loader la-2x">
              <div></div>
            </div>
          </div>
        </div>
      )}
      <div className="header bg-gradient-primary pb-8 pt-4" />
      <div className="dashboard-overview-inner container-fluid">
        {pendingUpdates && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity="info"
              action={
                <Button
                  color="info"
                  size="small"
                  variant="contained"
                  onClick={() => {
                    if (pendingUpdates.dashboardData) dispatch(updateDashboardData(pendingUpdates.dashboardData))
                    if (pendingUpdates.paymentsData) dispatch(updatePaymentsData(pendingUpdates.paymentsData))
                    if (pendingUpdates.dauData) dispatch(updateDauData(pendingUpdates.dauData))
                    if (pendingUpdates.peakData) dispatch(updatePeakData(pendingUpdates.peakData))
                    setPendingUpdates(null)
                  }}
                >
                  Update View
                </Button>
              }
            >
              New dashboard updates are available.
            </Alert>
          </Box>
        )}
        <div className="dashboard-toolbar">
          <Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Platform health, engagement, and revenue at a glance
            </Typography>
          </Box>
          <div className="dashboard-toolbar-dates">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disableFuture
                label="From date"
                value={startDate}
                onChange={setStartDate}
                maxDate={endDate}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                disableFuture
                label="To date"
                value={endDate}
                onChange={setEndDate}
                minDate={startDate}
                slotProps={{ textField: { size: 'small' } }}
              />
            </LocalizationProvider>
          </div>
          <div className="dashboard-toolbar-actions">
            <Button variant="contained" onClick={handleFilter}>
              Apply filter
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              Reset
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleFilter}
            >
              Refresh
            </Button>
          </div>
        </div>

        <MoreInsightsSection dashboardData={dashboardData} />

        <p className="dashboard-section-title">User active time</p>
        <ActiveTimeSection dashboardData={dashboardData} />

        <p className="dashboard-section-title">Payments summary</p>
        <PaymentsSummarySection
          isLoading={paymentsLoading}
          error={paymentsError}
          paymentsData={paymentsData}
          onRetry={() => fetchPayments()}
        />


        <p className="dashboard-section-title">Key metrics</p>
        <Grid container spacing={1.5} className="dashboard-grid" sx={{ mb: 1.5 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              accent="#5e72e4"
              icon={<PeopleIcon fontSize="small" />}
              label="Today's daily active users"
              value={formatIndianNumber(dashboardData?.dauCount ?? 0, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              })}
              sub={
                dashboardData?.totalLocationActive != null
                  ? `${formatIndianNumber(dashboardData.totalLocationActive, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  })} location-active`
                  : 'Active today'
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              accent="#2dce89"
              icon={<VerifiedIcon fontSize="small" />}
              label="Verification badges"
              value={formatIndianNumber(
                parseMetricValue(dashboardData?.totalNumberOfVerificationBadge),
                { minimumFractionDigits: 0, maximumFractionDigits: 0 }
              )}
              sub="Total issued"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              accent="#11cdef"
              icon={<SubscriptionsIcon fontSize="small" />}
              label="Subscribed channels"
              value={formatIndianNumber(
                parseMetricValue(dashboardData?.totalNumberOfSubscribedChannel),
                { minimumFractionDigits: 0, maximumFractionDigits: 0 }
              )}
              sub="Channel subscriptions"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              accent="#fb6340"
              icon={<MonetizationOnIcon fontSize="small" />}
              label="Tips received (beads)"
              value={dashboardData?.totalNumberOfTipReceived || '0'}
              sub="NGN equivalent in beads"
            />
          </Grid>
        </Grid>

        <p className="dashboard-section-title">Referral rewards (beads)</p>
        <Grid container spacing={1.5} className="dashboard-grid" sx={{ mb: 1.5 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              accent="#8965e0"
              icon={<MonetizationOnIcon fontSize="small" />}
              label="Total referral rewards"
              value={dashboardData?.totalReferralRewardsCoins || '0'}
              sub="All time"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              accent="#8965e0"
              icon={<MonetizationOnIcon fontSize="small" />}
              label="Last 30 days"
              value={dashboardData?.monthlyReferralRewardCoins || '0'}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              accent="#8965e0"
              icon={<MonetizationOnIcon fontSize="small" />}
              label="Last 15 days"
              value={dashboardData?.twoWeeksReferralRewardCoins || '0'}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              accent="#8965e0"
              icon={<MonetizationOnIcon fontSize="small" />}
              label="Last 7 days"
              value={dashboardData?.oneWeekReferralRewardCoins || '0'}
            />
          </Grid>
        </Grid>

        <p className="dashboard-section-title">Engagement trends</p>
        <Grid container spacing={2} className="dashboard-grid" sx={{ mb: 2 }}>
          <Grid item xs={12} lg={8}>
            <Card className="dashboard-chart-card">
              <CardContent sx={{ pb: 0 }}>
                <Typography variant="h6" fontWeight={700}>
                  Daily active users
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  User activity over the selected date range
                </Typography>
              </CardContent>
              <div className="chart-body">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dauData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={v => (v ? dayjs(v).format('DD MMM') : '')}
                    />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip contentStyle={ChartTooltipStyle} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="Active users"
                      stroke="#5e72e4"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Card className="dashboard-chart-card">
              <CardContent sx={{ pb: 0 }}>
                <Typography variant="h6" fontWeight={700}>
                  Peak usage by hour
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(endDate || dayjs()).format('DD MMM YYYY')}
                </Typography>
              </CardContent>
              <div className="chart-body">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={peakData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                    <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip contentStyle={ChartTooltipStyle} />
                    <Bar dataKey="count" name="Users" fill="#2dce89" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Grid>
        </Grid>

        <p className="dashboard-section-title">Platform snapshot</p>
        <Grid container spacing={2} className="dashboard-grid" sx={{ mb: 2 }}>
          <Grid item xs={12} lg={7}>
            <Card className="dashboard-chart-card">
              <CardContent sx={{ pb: 0 }}>
                <Typography variant="h6" fontWeight={700}>
                  Users & content
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Compare major platform counts side by side
                </Typography>
              </CardContent>
              <div className="chart-body">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={platformMetrics}
                    layout="vertical"
                    margin={{ top: 8, right: 56, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={formatCompact} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={130}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(94, 114, 228, 0.08)' }}
                      contentStyle={ChartTooltipStyle}
                      formatter={v => [formatCompact(v), 'Count']}
                    />
                    <Bar dataKey="value" name="Count" radius={[0, 4, 4, 0]}>
                      {platformMetrics.map((entry, index) => (
                        <Cell key={entry.name} fill={entry.fill || CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                      <LabelList
                        dataKey="value"
                        position="right"
                        formatter={formatCompact}
                        style={{ fontSize: 12, fontWeight: 600, fill: '#525f7f' }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Card className="dashboard-chart-card">
              <CardContent sx={{ pb: 0 }}>
                <Typography variant="h6" fontWeight={700}>
                  Revenue (beads)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tips vs admin earnings
                </Typography>
              </CardContent>
              <div className="chart-body dashboard-revenue-panel">
                <RevenueComparePanel items={revenueMetrics} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
                  Bars show proportion relative to tips received. Amounts are shown in full above each bar.
                </Typography>
              </div>
            </Card>
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

export default DashboardOverview
